import { useGetCustomColormaps } from "@/api/hooks/customColormaps";
import { SingleDropdown, type SingleDropdownOption } from "@/components/molecules/SingleDropdown/SingleDropdown";
import type { LayerUnion } from "@/layers";
import { FormField } from "@/overlay/components/form-field";
import type { MapId } from "@/types/type-utils";
import { isColorMap } from "@/utility/typeGuard";
import { unsafeGetColormapByName } from "@/utility/unsafeGetColormapByName";
import { t } from "@lingui/core/macro";
import { ColorMap, colormaps } from "@mm/api.meteomatics.com";
import type { WmsLayer } from "@mm/metx-workbench.meteomatics.com";
import { useMemo } from "react";

export function WMSLayerSelect(props: {
  id: MapId;
  layer: WmsLayer;
  change: (propsValue: Partial<LayerUnion>) => void;
  showCustomColormaps?: boolean;
  additionalColorMaps?: readonly SingleDropdownOption<string>[];
}) {
  const colorMaps: ColorMap[] = colormaps;
  const defaultColormap = unsafeGetColormapByName("", props.layer.parameter_unit);
  const requestedColorMap =
    props.layer.color_map && props.layer.color_map !== "" ? props.layer.color_map : defaultColormap;
  const { list: customColormaps } = useGetCustomColormaps(props.showCustomColormaps);
  const hasSelectedCustomColormap = (customColormaps ?? []).some((colorMap) => colorMap.id === requestedColorMap);
  const hasSelectedAdditionalColormap = (props.additionalColorMaps ?? []).some(
    (colorMap) => colorMap.value === requestedColorMap,
  );
  const hasUnsupportedCustomColormap =
    !isColorMap(requestedColorMap) && !hasSelectedCustomColormap && !hasSelectedAdditionalColormap;
  const selectedColorMap = hasUnsupportedCustomColormap ? defaultColormap : requestedColorMap;
  const dropdownOptions = useMemo<SingleDropdownOption<string>[]>(() => {
    const options: SingleDropdownOption<string>[] = [];

    if (props.showCustomColormaps) {
      options.push({
        value: "__custom_colormaps__",
        label: t`-- Custom Colormaps --`,
        disabled: true,
      });
      options.push(
        ...(customColormaps ?? []).map((colorMap) => ({
          value: colorMap.id,
          label: colorMap.label,
        })),
      );
    }

    options.push({
      value: "__available_colormaps__",
      label: t`-- Available Colormaps --`,
      disabled: true,
    });
    options.push(...(props.additionalColorMaps ?? []));
    options.push(
      ...colorMaps.map((colorMap) => ({
        value: colorMap,
        label: `${ColorMap[colorMap]} ${colorMap === defaultColormap ? t`(default)` : ""}`,
      })),
    );

    return options;
  }, [props.showCustomColormaps, props.additionalColorMaps, customColormaps, colorMaps, defaultColormap]);

  return (
    <FormField label={t`Color Map`}>
      <SingleDropdown
        options={dropdownOptions}
        value={selectedColorMap}
        onChange={(value) => props.change({ color_map: value })}
      />
    </FormField>
  );
}
